import { Button, Col, Form, Input, Modal, Radio, Row, Spin } from 'antd';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { UserSharingDataEmailFrequency, UserSharingDataSettingsItem, UserSharingDataSettingsStore } from 'src/stores/users-store';
import { PlusOutlined } from '@ant-design/icons';
import { validateEmail } from 'src/core/utils/object';

const UserSharingDataSettingsModal: FC<{ onCloseModal: () => void }> = ({ onCloseModal }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [okButtonLoading, setOkButtonLoading] = useState(false);
  const [formIsValid, setFormIsValid] = useState(true);
  const userSharingDataSettingsStore = container.get(UserSharingDataSettingsStore);
  const userSharingDataSettingsState = userSharingDataSettingsStore.state;

  const [emails, setEmails] = useState<string[]>([]);
  const emailsReference = useRef<string[]>([]);
  const [frequency, setFrequency] = useState<UserSharingDataEmailFrequency>(UserSharingDataEmailFrequency.Weekly);

  const [form] = Form.useForm();

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    form.resetFields();
    validateForm();
  }, [emails]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateForm = async () => {
    try {
      await form.validateFields();
      setFormIsValid(true);
    } catch {
      setFormIsValid(false);
    }
  };

  const load = async () => {
    await userSharingDataSettingsStore.loadSettings();
    const storeEmails = (userSharingDataSettingsState.item.value as UserSharingDataSettingsItem).EmailAddress;
    setEmails(storeEmails);
    emailsReference.current = storeEmails;
    setFrequency(userSharingDataSettingsState.item.value.frequency);
    setIsLoading(false);
  };

  return (
    <Modal
      title={t('SharingDataSettings')}
      open
      onCancel={() => onCloseModal()}
      width={600}
      okButtonProps={{ loading: okButtonLoading, disabled: !formIsValid }}
      onOk={async () => {
        setOkButtonLoading(true);
        const item: UserSharingDataSettingsItem = {
          EmailAddress: emailsReference.current,
          frequency: frequency,
        };
        userSharingDataSettingsStore.updateSettings(item, userSharingDataSettingsState.item.id.value);
        setOkButtonLoading(false);
        onCloseModal();
      }}
    >
      <Spin spinning={isLoading}>
        {!isLoading && emails && frequency && (
          <Row>
            <Col lg={18} xs={24}>
              <span>{t('EmailAddresses')}</span>
              <div style={{ height: '40vh', overflowY: 'auto', marginTop: 10 }}>
                <Form form={form}>
                  {emails.map((v, index) => (
                    <Row style={{ width: '90%' }} key={`row-${index}`}>
                      <Col span={22}>
                        <Form.Item
                          initialValue={v}
                          name={`item-${index}`}
                          style={{ marginBottom: 0 }}
                          rules={[
                            () => ({
                              required: true,
                              validator(_, value) {
                                if (!validateEmail(value)) return Promise.reject(t('InvalidEmailWarning'));
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Input
                            key={`email-${index}`}
                            style={{ marginTop: 8 }}
                            onChange={(event) => {
                              emailsReference.current = [
                                ...emailsReference.current.slice(undefined, index),
                                event.target.value,
                                ...emailsReference.current.slice(index + 1),
                              ];
                              validateForm();
                            }}
                          ></Input>
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Button
                          type="dashed"
                          className="solid-button"
                          style={{ height: '40px', marginTop: 8, marginLeft: 4 }}
                          onClick={() => {
                            const newEmails = [...emailsReference.current.slice(undefined, index), ...emailsReference.current.slice(index + 1)];
                            emailsReference.current = newEmails;
                            setEmails(newEmails);
                          }}
                        >
                          x
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </Form>
                <Button
                  type="dashed"
                  style={{ width: '94%', height: 45, marginBottom: 20, marginTop: 8 }}
                  onClick={async () => {
                    emailsReference.current = [...emailsReference.current, ''];
                    setEmails(emailsReference.current);
                  }}
                >
                  <PlusOutlined />
                </Button>
              </div>
            </Col>
            <Col lg={6} xs={24}>
              <div style={{ marginLeft: 10 }}>
                <span>{t('EmailFrequency')}</span>
                <br />
                <Radio.Group
                  name="email-frequency"
                  style={{ marginTop: 5 }}
                  defaultValue={userSharingDataSettingsState.item.value.frequency}
                  onChange={(event) => setFrequency(event.target.value)}
                >
                  <Radio value={UserSharingDataEmailFrequency.Weekly}>{t('Weekly')}</Radio>
                  <Radio value={UserSharingDataEmailFrequency.Monthly}>{t('Monthly')}</Radio>
                  <Radio value={UserSharingDataEmailFrequency.Quaterly}>{t('Quarterly')}</Radio>
                </Radio.Group>
              </div>
            </Col>
          </Row>
        )}
      </Spin>
    </Modal>
  );
};

export default UserSharingDataSettingsModal;
